/**
 * Top-right strip map (08 §2.7): a north-up sliver of the mission with the ranger, the pickup X,
 * the goal zone, the objective targets and any supply pod still lying on the ground - without that
 * last one a captured start (10 §2, mission 32) is a hunt for kit whose position only the sim knows.
 * The ground is drawn once (`mapBackdrop.ts`); the rest at most 10 Hz. What each mark MEANS is
 * decided in `mapMarks.ts`, without a canvas, so the tests can hold it to the briefing.
 */

import './stripMap.css';
import type { MissionDef, World } from '../../sim/types';
import { clamp, el } from './dom';
import { drawBackdrop } from './mapBackdrop';
import {
  extraMarks, graveOf, HOT_RING, hotPickup, MAP_COLOURS, marksKey, targetMarks, type TargetRole,
  type TargetState, targetStateOf, toPixel,
} from './mapMarks';
import { radioSilent } from './missionConditions';

/** Largest footprint in logical pixels; the mission's aspect ratio is kept. */
const MAP_MAX_W = 44;
const MAP_MAX_H = 116;
const MAP_INTERVAL_MS = 100;

/** A mission target on the map; `index` caches where its state sits in `world.props`. */
interface TargetSlot {
  id: number;
  x: number;
  z: number;
  role: TargetRole;
  index: number;
  state: TargetState;
}

function targetStateIn(world: World, target: TargetSlot): TargetState {
  let state = world.props[target.index];
  if (state === undefined || state.id !== target.id) {
    target.index = world.props.findIndex((prop) => prop.id === target.id);
    state = world.props[target.index];
  }
  return targetStateOf(state);
}

export class StripMap {
  readonly el: HTMLElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly backdrop: HTMLCanvasElement;
  /** Map pixels per metre. */
  private readonly scale: number;
  private readonly width: number;
  private readonly height: number;
  private readonly targets: TargetSlot[];
  /** Where a fallen ranger's tags lie, if this mission carries them. */
  private readonly grave: { x: number; z: number } | null = null;
  private drawnAt = Number.NEGATIVE_INFINITY;
  private key = '';
  private colourBlind = false;
  /** Radio Silence (04 §7): no map and no markers. The panel stays, to say why there is nothing in it. */
  readonly silent: boolean;
  /** Hot Extraction (04 §7): the X is contested. A modifier never changes, so it is read once. */
  private readonly hotLz: boolean;
  /** Alternate LZ (04 §8): the second point, and where the mission first put the first one. */
  private readonly altLz: { x: number; z: number } | null;
  private readonly homeLz: { x: number; z: number };

  constructor(def: MissionDef) {
    this.hotLz = hotPickup(def);
    this.altLz = def.pickupAlt ?? null;
    this.homeLz = { x: def.pickup.x, z: def.pickup.z };
    this.scale = Math.min(MAP_MAX_W / Math.max(1, def.sizeX), MAP_MAX_H / Math.max(1, def.sizeZ));
    this.width = Math.max(8, Math.round(def.sizeX * this.scale));
    this.height = Math.max(8, Math.round(def.sizeZ * this.scale));
    this.el = el('div', 'hud-panel hud-tr');
    this.silent = radioSilent(def);
    if (this.silent) {
      this.el.classList.add('hud-map-silent');
      const words = ['RADIO', 'SILENCE'].map((word) => el('div', 'hud-map-north', word));
      this.el.append(...words, el('div', 'hud-map-off', 'NO MAP'));
      this.ctx = null;
      this.backdrop = document.createElement('canvas');
      this.targets = [];
      this.grave = null;
      return;
    }
    const canvas = el('canvas', 'hud-map');
    canvas.width = this.width;
    canvas.height = this.height;
    canvas.style.width = `${this.width}px`;
    canvas.style.height = `${this.height}px`;
    this.el.append(el('div', 'hud-map-north', 'N'), canvas);
    this.ctx = canvas.getContext('2d');
    if (this.ctx !== null) this.ctx.imageSmoothingEnabled = false;
    this.backdrop = drawBackdrop(def, this.width, this.height, this.scale);
    this.targets = targetMarks(def).map((mark) => ({ ...mark, index: -1, state: 'live' as TargetState }));
    // Tag Recovery (04 §2.3): the briefing says his marker "stands somewhere on the map" and then
    // nothing ever showed it - a 1.2 m prop 25-60 m off the route, at night, in the rain. NOT
    // `target: true`: that would paint it red, and a dead friend's grave is not a thing to destroy.
    this.grave = graveOf(def);
  }

  /** Swap the target marks to the colour-blind-safe accent; takes effect on the next draw. */
  setColourBlind(on: boolean): void {
    if (on === this.colourBlind) return;
    this.colourBlind = on;
    this.key = '';
    this.drawnAt = Number.NEGATIVE_INFINITY;
  }

  /** Redraw if 100 ms have passed and something on the map actually changed. */
  draw(world: World, now: number): void {
    const g = this.ctx;
    if (g === null || now - this.drawnAt < MAP_INTERVAL_MS) return;
    this.drawnAt = now;

    const rx = clamp(toPixel(world.ranger.x, this.scale), 1, this.width - 2);
    const rz = clamp(toPixel(world.ranger.z, this.scale), 1, this.height - 2);
    // One letter per target: live, wrecked and GONE are three marks now, so a count would not do.
    let states = '';
    for (const target of this.targets) {
      target.state = targetStateIn(world, target);
      states += target.state[0];
    }
    // A pod worth walking to: on the ground and not yet emptied.
    const pods = world.insertion.pods.filter((p) => (p.state === 'landed' || p.state === 'falling')
      && (p.contents.magazines + p.contents.grenades + p.contents.laws + p.contents.bombs + p.contents.firstAid) > 0);
    const phase = world.osprey.phase;
    const live = phase === 'inbound' || phase === 'hovering';
    const pickupMode = !live ? 0 : Math.floor(now / 350) % 2 === 0 ? 1 : 2;
    // The prisoners and the grave stand still, but the man of the mission WALKS - and is then driven
    // off in a car - so a count would draw him once and freeze him there. The key carries every extra
    // mark's drawn pixel, which is exactly what would be stale if it did not.
    const extras = extraMarks(world, this.grave);
    // The X is not always where the mission put it: `relocatesToCapture` moves it (04 §2), so it is keyed too.
    const px = toPixel(world.objective.pickupX, this.scale);
    const pz = toPixel(world.objective.pickupZ, this.scale);
    // Alternate LZ (04 §8): both X's while the choice is still open, one the moment the Osprey has taken
    // it (`sim/conditions.chooseLandingZone`). A second X left on the map after the aircraft has settled
    // would send him to ground nothing is coming to - which is the card's old lie, drawn better.
    const settled = phase !== 'away'
      || world.objective.pickupX !== this.homeLz.x || world.objective.pickupZ !== this.homeLz.z;
    const alt = settled ? null : this.altLz;
    const key = `${rx},${rz},${states},${pickupMode},${px}:${pz},${alt === null ? '' : 'a'},`
      + `${pods.length},${marksKey(extras, this.scale)}`;
    if (key === this.key) return;
    this.key = key;

    g.clearRect(0, 0, this.width, this.height);
    g.drawImage(this.backdrop, 0, 0);
    for (const target of this.targets) this.drawTarget(g, target);

    // Supply pods: a small green box, the colour of the beacon on the pod itself.
    g.fillStyle = MAP_COLOURS.pod;
    for (const pod of pods) {
      const x = clamp(toPixel(pod.x, this.scale), 1, this.width - 2);
      const z = clamp(toPixel(pod.z, this.scale), 1, this.height - 2);
      g.fillRect(x - 1, z - 1, 3, 3);
      g.clearRect(x, z, 1, 1);
    }

    // People still to reach, a fallen ranger's marker, and the man the mission is about. A cross for a
    // person, a hollow box for the grave, a solid block for the man: shape as well as colour, so none
    // of them rests on hue alone (08 §4). The man keeps the block when he goes down and only changes
    // colour - red while he can fight, the go-here teal once all that is left is to take the satchel.
    for (const mark of extras) {
      const x = clamp(toPixel(mark.x, this.scale), 1, this.width - 2);
      const z = clamp(toPixel(mark.z, this.scale), 1, this.height - 2);
      if (mark.kind === 'captive') {
        g.fillStyle = MAP_COLOURS.captive;
        g.fillRect(x - 1, z, 3, 1);
        g.fillRect(x, z - 1, 1, 3);
      } else if (mark.kind === 'tags') {
        g.fillStyle = MAP_COLOURS.tags;
        g.fillRect(x - 1, z - 1, 3, 3);
        g.clearRect(x, z - 1, 1, 2);
      } else {
        const fighting = mark.kind === 'man';
        g.fillStyle = !fighting ? MAP_COLOURS.waypoint
          : this.colourBlind ? MAP_COLOURS.targetSafe : MAP_COLOURS.target;
        g.fillRect(x - 1, z - 1, 3, 3);
        g.fillStyle = MAP_COLOURS.targetCore;
        g.fillRect(x, z, 1, 1);
      }
    }

    // Hot Extraction (04 §7): a broken ring round the X, from the first frame. The X's own white-then-
    // green already says whether the Osprey is coming, so a third colour there would muddle it; the ring
    // says something else entirely - that the garrison comes to the X when it does, and that the Osprey
    // waits less. The moment he most needs to know which way to run is when it has all gone wrong.
    if (this.hotLz) {
      const hx = clamp(px, 3, this.width - 4);
      const hz = clamp(pz, 3, this.height - 4);
      g.fillStyle = MAP_COLOURS.danger;
      for (const [dx, dz] of HOT_RING) g.fillRect(hx + dx, hz + dz, 1, 1);
    }

    // The pickup X; it pulses green once the Osprey has been called. The alternate is drawn in the same
    // white, never the green: the green says "the aircraft is coming HERE", and it never comes to both.
    if (alt !== null) {
      this.drawX(g, toPixel(alt.x, this.scale), toPixel(alt.z, this.scale), MAP_COLOURS.pickup);
    }
    if (pickupMode !== 2) {
      this.drawX(g, px, pz, pickupMode === 1 ? MAP_COLOURS.pickupLive : MAP_COLOURS.pickup);
    }

    g.fillStyle = MAP_COLOURS.ink;
    g.fillRect(rx - 2, rz - 2, 5, 5);
    g.fillStyle = MAP_COLOURS.ranger;
    g.fillRect(rx - 1, rz - 1, 3, 3);
  }

  /** An extraction point: the five-pixel X the player looks for when it has all gone wrong. */
  private drawX(g: CanvasRenderingContext2D, x: number, z: number, colour: string): void {
    const cx = clamp(x, 2, this.width - 3);
    const cz = clamp(z, 2, this.height - 3);
    g.fillStyle = colour;
    for (let i = -2; i <= 2; i++) {
      g.fillRect(cx + i, cz + i, 1, 1);
      g.fillRect(cx + i, cz - i, 1, 1);
    }
  }

  /**
   * Four marks, each its own shape as well as its own colour (08 §4): a solid block for a thing to
   * destroy, an open ring for a place to reach or work, a grey ring for a wreck, and an X for a
   * target that LEFT whole - `targetsLeave` wrecks nothing, and painting it grey told the player the
   * job was done by the very rule that had just lost it for him (ROUND5 §1.3).
   */
  private drawTarget(g: CanvasRenderingContext2D, target: TargetSlot): void {
    const wide = target.state === 'live' && target.role === 'reach';
    const pad = wide ? 2 : 1;
    const x = clamp(Math.round(target.x * this.scale), pad, this.width - pad - 1);
    const z = clamp(Math.round(target.z * this.scale), pad, this.height - pad - 1);
    if (target.state === 'gone') {
      g.fillStyle = MAP_COLOURS.danger;
      for (let i = -1; i <= 1; i++) {
        g.fillRect(x + i, z + i, 1, 1);
        g.fillRect(x + i, z - i, 1, 1);
      }
    } else if (target.state === 'wrecked') {
      g.fillStyle = MAP_COLOURS.wreck;
      g.fillRect(x - 1, z - 1, 3, 3);
      g.clearRect(x, z, 1, 1);
    } else if (wide) {
      g.fillStyle = MAP_COLOURS.waypoint;
      g.fillRect(x - 2, z - 2, 5, 5);
      g.clearRect(x - 1, z - 1, 3, 3);
      g.fillRect(x, z, 1, 1);
    } else {
      g.fillStyle = this.colourBlind ? MAP_COLOURS.targetSafe : MAP_COLOURS.target;
      g.fillRect(x - 1, z - 1, 3, 3);
      g.fillStyle = MAP_COLOURS.targetCore;
      g.fillRect(x, z, 1, 1);
    }
  }
}
